import React from "react";
import ActivityLine from "../../ActivityLine/ActivityLine";
import Tooltip from "../../../../../components/Tooltip/Tooltip";
import type { DepGraph, DepGraphActivity } from "../../../../../models";
import type {
  DepCandidate,
  ExternalCandidate,
} from "../../DepChipTypeahead/DepChipTypeahead";
import {
  activityLabel,
  driverStripsHeight,
  kindFromTaskType,
  nodeStatus,
  predsFor,
  ROW_H,
  rowFinish,
  rowStart,
  type ResolvedSucc,
  type RowDriver,
} from "../../graphRows";
import ChipCell from "../ChipCell/ChipCell";
import DurationCell from "../DurationCell/DurationCell";
import type { DepEditIntents } from "../intents";

const SELECT_IGNORE_SELECTOR = "button, input, .dep-chip-popover, .dep-typeahead";

interface Props {
  activity: DepGraphActivity;
  predDrivers: RowDriver[];
  succDrivers: RowDriver[];
  isSelected: boolean;
  /** "pred"/"succ" when this row is a direct predecessor / successor of the
   *  selected row — tints it light red / light yellow. null otherwise. */
  neighborRole: "pred" | "succ" | null;
  graph: DepGraph;
  succs: ResolvedSucc[];
  anchorsTable: boolean;
  /** Compact table pills (code + relationship only). */
  compact: boolean;
  canSelect: boolean;
  canDelete: boolean;
  tableReadOnly: boolean;
  allowExternalEdits: boolean;
  canEditRows: boolean;
  cycleNodes?: Set<string>;
  cycleEdges?: Set<string>;
  zonesByCode: Map<string, Set<string>>;
  allCandidates: DepCandidate[];
  fetchExternals?: (query: string) => Promise<ExternalCandidate[]>;
  intents: DepEditIntents;
  anchorTags?: Map<string, "in" | "out" | "both">;
  onEditDuration?: (id: string, days: number) => void;
  onDuplicate?: (activityId: string) => void;
  onDeleteActivity?: (id: string) => void;
  onInsertBelow?: (activityId: string) => void;
  selectableId: (a: DepGraphActivity) => boolean;
  fmtDate: (iso: string | null) => string;
  onSelect: (id: string) => void;
  onStartEdit: (id: string) => void;
  attachRowRef: (el: HTMLTableRowElement | null) => void;
}

const ActivityRow: React.FC<Props> = ({
  activity: act,
  predDrivers,
  succDrivers,
  isSelected,
  neighborRole,
  graph,
  succs,
  anchorsTable,
  compact,
  canSelect,
  canDelete,
  tableReadOnly,
  allowExternalEdits,
  canEditRows,
  cycleNodes,
  cycleEdges,
  zonesByCode,
  allCandidates,
  fetchExternals,
  intents,
  anchorTags,
  onEditDuration,
  onDuplicate,
  onDeleteActivity,
  onInsertBelow,
  selectableId,
  fmtDate,
  onSelect,
  onStartEdit,
  attachRowRef,
}) => {
  const preds = predsFor(graph, act.id);
  const kind = kindFromTaskType(act.task_type);
  const isMilestone = kind === "milestone";
  const isNonTask = kind !== "task";
  const readOnly = !act.in_scope;
  // "Uncoded" = in-section work with no work code yet (tinted + dashed).
  // An activity that's been given a code (e.g. auto-takted) reads as
  // coded even before it's placed in a zone.
  const isUntagged = act.in_scope && !act.work_code && !isNonTask;
  const status = nodeStatus(act);

  const rowClass = [
    cycleNodes?.has(act.id) ? "dep-table-row--cycle" : "",
    isSelected ? "dep-table-row--selected" : "",
    neighborRole === "pred" ? "dep-table-row--pred" : "",
    neighborRole === "succ" ? "dep-table-row--succ" : "",
    isNonTask ? "dep-table-row--non-task" : "",
    readOnly ? "dep-table-row--context" : "",
    isUntagged ? "dep-table-row--neighbour" : "",
    act.is_new ? "dep-table-row--new" : "",
    status === "complete" ? "dep-table-row--complete" : "",
  ].filter(Boolean).join(" ") || undefined;

  // Columns show the RETAINED (remaining-early / driving) dates — what the
  // schedule and its arrows actually drive off — consistently in both Board and
  // Forward-pass mode. For active work that's the remaining early start, not the
  // actual start (which shows as the shaded part of the bar).
  const dateNote =
    status === "complete"
      ? "Retained CPM point it drives from (the actual finish shows on the bar)"
      : status === "active"
        ? "Remaining (retained) early start → remaining early finish — where the schedule drives from; the actual start is the shaded part of the bar"
        : "Early (planned) start/finish";

  const glyph = isMilestone ? "◆" : "○";
  const typeLabel = act.task_type ? ` (${act.task_type})` : "";

  const rowSelectable = !tableReadOnly && selectableId(act);
  const canEditThisDur =
    !tableReadOnly &&
    Boolean(onEditDuration) && act.in_scope && !isNonTask && status !== "complete";

  return (
    <tr
      ref={attachRowRef}
      className={rowClass}
      style={{
        height:
          ROW_H + driverStripsHeight(predDrivers.length, succDrivers.length),
      }}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest(SELECT_IGNORE_SELECTOR)) return;
        onSelect(act.id);
      }}
    >
      {canSelect && <td className="dep-table-col-select" />}
      <td className="dep-table-col-name">
        <div className="dep-table-name-row">
          {isNonTask ? (
            <Tooltip content={`${act.task_code} · ${act.name}${typeLabel}`}>
              <span className="dep-table-non-task-pill">
                <span className="dep-table-non-task-glyph" aria-hidden="true">{glyph}</span>
                {act.work_code && <span className="dep-table-non-task-code">{act.work_code}</span>}
                <span className="activity-line-name">{act.name}</span>
              </span>
            </Tooltip>
          ) : (
            <ActivityLine
              workCode={act.work_code}
              name={activityLabel(act)}
              fullName={act.name}
              taskCode={act.task_code}
              color={act.color}
              status={status}
              marker={anchorTags?.get(act.id) ?? null}
              origin={readOnly ? act.section || "outside" : null}
            />
          )}
          <span className="dep-table-row-actions">
            {canEditRows && rowSelectable && !isNonTask && (
              <button
                type="button"
                className="dep-table-rename-btn"
                title="Edit this activity — work code, name, days"
                onClick={() => onStartEdit(act.id)}
              >
                ✎
              </button>
            )}
            {onDuplicate && !tableReadOnly && act.in_scope && !isNonTask && (
              <button
                type="button"
                className="dep-table-duplicate-btn"
                title="Duplicate this activity"
                onClick={() => onDuplicate(act.id)}
              >
                ⧉
              </button>
            )}
          </span>
        </div>
      </td>
      <td className="dep-table-col-date">
        <Tooltip content={`Start · ${dateNote}`}>
          <span className="dep-table-date">{fmtDate(rowStart(act))}</span>
        </Tooltip>
      </td>
      <td className="dep-table-col-date">
        <Tooltip content={`Finish · ${dateNote}`}>
          <span className="dep-table-date">{fmtDate(rowFinish(act))}</span>
        </Tooltip>
      </td>
      <td className="dep-table-col-dur">
        {isNonTask ? (
          "—"
        ) : canEditThisDur ? (
          <DurationCell
            days={act.duration_days}
            onCommit={(d) => onEditDuration!(act.id, d)}
          />
        ) : (
          `${act.duration_days}d`
        )}
      </td>
      <td className="dep-table-col-chips">
        <ChipCell
          row={act}
          side="pred"
          compact={compact}
          preds={preds}
          succs={succs}
          readOnly={tableReadOnly || readOnly}
          allowExternalEdits={allowExternalEdits && !tableReadOnly}
          zonesByCode={zonesByCode}
          allCandidates={allCandidates}
          fetchExternals={fetchExternals}
          intents={intents}
        />
        {anchorsTable && (
          <ChipCell
            cycleEdges={cycleEdges}
            row={act}
            side="succ"
            compact={compact}
            preds={preds}
            succs={succs}
            readOnly={tableReadOnly || readOnly}
            allowExternalEdits={allowExternalEdits && !tableReadOnly}
            zonesByCode={zonesByCode}
            allCandidates={allCandidates}
            fetchExternals={fetchExternals}
            intents={intents}
          />
        )}
      </td>
      {!anchorsTable && (
        <td className="dep-table-col-chips">
          <ChipCell
            cycleEdges={cycleEdges}
            row={act}
            side="succ"
            compact={compact}
            preds={preds}
            succs={succs}
            readOnly={tableReadOnly || readOnly}
            allowExternalEdits={allowExternalEdits && !tableReadOnly}
            zonesByCode={zonesByCode}
            allCandidates={allCandidates}
            fetchExternals={fetchExternals}
            intents={intents}
          />
        </td>
      )}
      {canDelete && (
        <td className="dep-table-col-actions">
          {rowSelectable && onInsertBelow && (
            <button
              type="button"
              className="dep-table-insert-btn"
              title="Insert an activity below this one"
              onClick={() => onInsertBelow(act.id)}
            >
              ＋
            </button>
          )}
          {rowSelectable && onDeleteActivity && (
            <button
              type="button"
              className="dep-table-delete-btn"
              title="Delete this activity"
              onClick={() => onDeleteActivity(act.id)}
            >
              ✕
            </button>
          )}
        </td>
      )}
    </tr>
  );
};

export default ActivityRow;
